/**
 * One tile-safe length of unmanaged hawthorn and blackthorn hedge.
 *
 * The end blobs fix the two-metre span while seeded interior lumps and bare
 * branches break the repeat. A hedge made from one box would be cheaper only in
 * the way a wall of green paint is cheaper: it loses the silhouette entirely.
 */
import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

export interface HedgeSectionOptions {
  readonly seed?: number;
}

const DEFAULT_SEED = 191;

function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function branch(from: THREE.Vector3, to: THREE.Vector3, material: THREE.Material, name: string): THREE.Mesh {
  const direction = to.clone().sub(from);
  const mesh = part(new THREE.CylinderGeometry(0.026, 0.040, direction.length(), 5), material, name);
  mesh.position.copy(from).addScaledVector(direction, 0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return mesh;
}

/** Build a deterministic section; its default seed is 191. */
export function buildHedgeSection(options: HedgeSectionOptions = {}): THREE.Group {
  const random = mulberry32(options.seed ?? DEFAULT_SEED);
  const group = new THREE.Group();
  group.name = 'hedge-section';
  const shadow = surface('organic', 0x3a5742);
  const dawn = surface('organic', 0x4f8f52);
  const bark = surface('organic', 0x4a4038);

  // The end lobes reach +/-1.02 m before the centring pass. Their symmetric
  // placement makes the join continuous; only the internal shape is random.
  const blobs = [
    { x: -0.79, y: 0.76, z: -0.02, sx: 0.23, sy: 0.58, sz: 0.43, material: shadow, name: 'west-end-dark' },
    { x: 0.79, y: 0.74, z: 0.03, sx: 0.23, sy: 0.56, sz: 0.42, material: shadow, name: 'east-end-dark' },
    { x: -0.36, y: 0.48, z: -0.07, sx: 0.50, sy: 0.48, sz: 0.45, material: shadow, name: 'low-dark' },
    { x: 0.27, y: 0.98, z: -0.08, sx: 0.54, sy: 0.76, sz: 0.46, material: shadow, name: 'high-dark' },
    { x: -0.13, y: 1.47, z: 0.16, sx: 0.49, sy: 0.65, sz: 0.39, material: dawn, name: 'top-dawn' },
    { x: 0.51, y: 1.25, z: 0.22, sx: 0.36, sy: 0.57, sz: 0.33, material: dawn, name: 'front-dawn' },
  ] as const;
  for (const definition of blobs) {
    const blob = part(new THREE.IcosahedronGeometry(1, 0), definition.material, definition.name);
    blob.position.set(
      definition.x + (random() - 0.5) * 0.07,
      definition.y + (random() - 0.5) * 0.08,
      definition.z + (random() - 0.5) * 0.06,
    );
    // End scales stay fixed so every seed meets cleanly; their positions may
    // vary only inward, keeping the outer x extent a reliable two metre tile.
    const endScale = definition.name.includes('end') ? 1 : 0.94 + random() * 0.12;
    blob.scale.set(definition.sx * endScale, definition.sy * endScale, definition.sz * endScale);
    group.add(blob);
  }

  const branchTips = [
    new THREE.Vector3(-0.40, 2.28, 0.06),
    new THREE.Vector3(0.38, 2.13, -0.30),
    new THREE.Vector3(0.84, 1.55, 0.37),
  ] as const;
  const branchRoots = [
    new THREE.Vector3(-0.22, 1.53, -0.02),
    new THREE.Vector3(0.14, 1.42, -0.04),
    new THREE.Vector3(0.56, 1.05, 0.10),
  ] as const;
  for (let index = 0; index < branchTips.length; index += 1) {
    const root = branchRoots[index];
    const tip = branchTips[index];
    if (!root || !tip) throw new Error('hedge-section: missing branch point');
    tip.x += (random() - 0.5) * 0.08;
    tip.z += (random() - 0.5) * 0.08;
    group.add(branch(root, tip, bark, `bare-branch-${index + 1}`));
  }

  const bounds = new THREE.Box3().setFromObject(group);
  group.position.set(
    -(bounds.min.x + bounds.max.x) / 2,
    -bounds.min.y,
    -(bounds.min.z + bounds.max.z) / 2,
  );
  return group;
}

export const hedgeSectionAsset: AssetDefinition = {
  id: 'hedge-section',
  label: 'Hedge section',
  description: 'A dense, lumpy, tile-safe unmanaged field hedge.',
  footprint: { width: 2.01, height: 2.22, length: 0.95 },
  create: (): THREE.Group => buildHedgeSection(),
};
